import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft, LucideEye, LucideEyeOff, LucideDownload,
  LucideChevronLeft, LucideChevronRight, LucideCheck, LucideLayoutGrid, LucideUsers, LucideSettings2,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import {
  fill as fillPDFProvincialGr912,
  SCHEMA_TO_PDF_FIELD,
  PAGE_INDEX_FOR_SCHEMA_FIELD,
  STEP_ANCHOR_FIELDS,
} from "./fill.js";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import { skillOptions } from "./options.js";
import { STEPS, STEP_FIELDS, COURSE_STEP_IDS, TOP_STEPS, COURSE_SUB_STEPS, CREDIT_ROWS } from "./steps.js";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AICommentButton } from "../shared/AICommentButton";
import { COURSE_CONFIG } from "./aiConfig";
import { FormFieldGrid } from "../shared/FormFieldGrid";

// ─── Reusable primitives ──────────────────────────────────────────────────────

const SKILL_ROWS = [
  { key: 'Responsibility',  label: 'Responsibility'   },
  { key: 'Organization',    label: 'Organization'     },
  { key: 'IndependentWork', label: 'Independent Work' },
  { key: 'Collaboration',   label: 'Collaboration'    },
  { key: 'Initiative',      label: 'Initiative'       },
  { key: 'SelfRegulation',  label: 'Self-Regulation'  },
];

// Renders one term column (First Term or Final Mark) inside a CourseCard.
function TermColumn({ label, prefix, termKey, control }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-center text-slate-400">{label}</p>

      {/* % Mark / Median / Credit Earned */}
      <div className="flex items-end gap-2">
        <FormField
          control={control}
          name={`${prefix}${termKey}PercentageMark`}
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-slate-400 uppercase tracking-wide">% Mark</span>
              <FormControl>
                <input
                  {...field}
                  maxLength={3}
                  inputMode="numeric"
                  className="w-14 h-10 text-center text-base font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${prefix}${termKey}Median`}
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-slate-400 uppercase tracking-wide">Median</span>
              <FormControl>
                <input
                  {...field}
                  maxLength={3}
                  inputMode="numeric"
                  className="w-14 h-9 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${prefix}${termKey}CreditEarned`}
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-slate-400 uppercase tracking-wide">Credit</span>
              <FormControl>
                <button
                  type="button"
                  onClick={() => field.onChange(field.value === 'yes' ? '' : 'yes')}
                  className={`w-10 h-9 rounded-lg border-2 text-xs font-bold transition-colors cursor-pointer ${
                    field.value === 'yes'
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                  }`}
                >
                  {field.value === 'yes' ? '✓' : '○'}
                </button>
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {/* Learning Skills */}
      <div className="space-y-1">
        {SKILL_ROWS.map(({ key, label: skillLabel }) => (
          <div key={key} className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-slate-500 w-24 shrink-0">{skillLabel}</span>
            <FormField
              control={control}
              name={`${prefix}${termKey}${key}`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PillSelector
                      size="sm"
                      value={field.value}
                      onChange={field.onChange}
                      options={skillOptions}
                      colorMap={LSWH_COLOR_MAP}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        ))}
      </div>

      {/* Attendance */}
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { fieldKey: `${prefix}${termKey}ClassesMissed`, label: 'Missed' },
          { fieldKey: `${prefix}${termKey}TotalClasses`,  label: 'Total'  },
          { fieldKey: `${prefix}${termKey}TimesLate`,     label: 'Late'   },
        ].map(({ fieldKey, label: attLabel }) => (
          <FormField
            key={fieldKey}
            control={control}
            name={fieldKey}
            render={({ field }) => (
              <FormItem className="flex flex-col items-center">
                <span className="text-[8px] text-slate-400 uppercase tracking-wide">{attLabel}</span>
                <FormControl>
                  <input
                    {...field}
                    maxLength={4}
                    inputMode="numeric"
                    className="w-full h-8 text-center text-sm border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
}

// Full course card: header (title/code/teacher + flags) + side-by-side First/Final columns + comments.
function CourseCard({ courseNum, semLabel, form, reportId }) {
  const n = courseNum;
  const prefix = `course${n}`;
  const { control } = form;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Header stripe */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
            {semLabel} · Course {n}
          </span>
        </div>

        {/* Title / Code / Teacher */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { name: `${prefix}Title`,   placeholder: 'Course Title',     colSpan: true  },
            { name: `${prefix}Code`,    placeholder: 'Code (e.g. ENG3U)', colSpan: false },
            { name: `${prefix}Teacher`, placeholder: 'Teacher Name',     colSpan: false },
          ].map(({ name, placeholder }) => (
            <FormField
              key={name}
              control={control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} placeholder={placeholder} className="text-sm" />
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
        </div>

        {/* Program flags */}
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Program Flags</p>
          <div className="flex gap-2">
            {['ESLELD', 'IEP', 'French', 'SHSM'].map((flag) => {
              const fieldName = `${prefix}${flag}`;
              return (
                <FormField
                  key={fieldName}
                  control={control}
                  name={fieldName}
                  render={({ field }) => (
                    <button
                      type="button"
                      onClick={() => field.onChange(field.value === 'yes' ? '' : 'yes')}
                      className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition-colors cursor-pointer ${
                        field.value === 'yes'
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {flag === 'ESLELD' ? 'ESL/ELD' : flag}
                    </button>
                  )}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* First Term / Final Mark side-by-side */}
      <div className="p-5">
        <div className="grid grid-cols-2 gap-0">
          <div className="pr-4 border-r border-slate-200">
            <TermColumn label="First Term" prefix={prefix} termKey="First" control={control} />
          </div>
          <div className="pl-4">
            <TermColumn label="Final Mark" prefix={prefix} termKey="Final" control={control} />
          </div>
        </div>

        {/* Comments + AI */}
        <div className="mt-5 pt-4 border-t border-slate-100">
          <AICommentButton
            config={COURSE_CONFIG(n)}
            form={form}
            fieldName={`${prefix}Comments`}
            reportId={reportId}
            label="Teacher Comments"
            placeholder="Teacher comments for this course…"
            labelClassName="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2"
            textareaClassName="min-h-[80px] resize-none text-sm"
            buttonClassName="border-teal-200 text-teal-700 hover:bg-teal-50"
          />
        </div>
      </div>
    </div>
  );
}

// Two-level step indicator: compact top row (5 nodes) + course sub-row.
function StepIndicator({ currentStep, completedSteps, onStepClick, courseLabels }) {
  const isCourseStep = COURSE_STEP_IDS.has(currentStep);

  return (
    <div>
      {/* ── Top row ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start w-full">
        {TOP_STEPS.map((step, index) => {
          const isCoursesNode = step.id === 'courses';
          const isLast = index === TOP_STEPS.length - 1;
          const isDone = isCoursesNode
            ? step.stepIds.every((id) => completedSteps.has(id))
            : completedSteps.has(step.id);
          const isCurrent = isCoursesNode ? isCourseStep : step.id === currentStep;

          const handleClick = () =>
            isCoursesNode ? onStepClick(step.jumpTo) : onStepClick(step.id);

          return (
            <div key={String(step.id)} className="flex items-center" style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
              <div className="flex flex-col items-center" style={{ minWidth: '28px' }}>
                <button
                  type="button"
                  onClick={handleClick}
                  title={isCoursesNode ? 'Courses' : String(step.label)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all cursor-pointer shrink-0 ${
                    isDone && !isCurrent
                      ? 'bg-teal-700 border-teal-700 text-white'
                      : isCurrent
                      ? 'bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50'
                      : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
                  }`}
                >
                  {isDone && !isCurrent ? (
                    <LucideCheck size={11} />
                  ) : isCoursesNode ? (
                    <LucideLayoutGrid size={12} />
                  ) : (
                    step.id
                  )}
                </button>
                <span
                  className={`text-[9px] mt-0.5 text-center leading-tight ${
                    isCurrent ? 'text-teal-700 font-bold' : isDone ? 'text-teal-500 font-medium' : 'text-slate-400 font-medium'
                  }`}
                  style={{ maxWidth: '44px', wordBreak: 'break-word' }}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className={`h-px flex-1 mx-0.5 mb-4 ${isDone ? 'bg-teal-500' : 'bg-slate-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Course sub-row ─────────────────────────────────────────────────── */}
      <div className="mt-2 mx-1 rounded-xl bg-slate-50/80 border border-slate-100 px-4 py-2">
        <div className="flex items-start w-full">
          {COURSE_SUB_STEPS.map((step, index) => {
            const isDone = completedSteps.has(step.id);
            const isCurrent = step.id === currentStep;
            const isLast = index === COURSE_SUB_STEPS.length - 1;
            const titleLabel = courseLabels?.[step.courseNum]?.slice(0, 4).trim() || `C${step.courseNum}`;
            return (
              <div key={step.id} className="flex items-center" style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
                <div className="flex flex-col items-center" style={{ minWidth: '22px' }}>
                  <button
                    type="button"
                    onClick={() => onStepClick(step.id)}
                    title={courseLabels?.[step.courseNum] || `Course ${step.courseNum}`}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-[1.5px] transition-all cursor-pointer shrink-0 ${
                      isDone && !isCurrent
                        ? 'bg-teal-500 border-teal-500 text-white'
                        : isCurrent
                        ? 'bg-teal-500 border-teal-500 text-white scale-125 ring-2 ring-inset ring-teal-300/50'
                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                    }`}
                  >
                    {isDone && !isCurrent ? <LucideCheck size={8} /> : step.courseNum}
                  </button>
                  <span
                    className={`text-[8px] mt-0.5 text-center leading-tight ${
                      isCurrent ? 'text-teal-600 font-bold' : isDone ? 'text-teal-400 font-medium' : 'text-slate-400 font-medium'
                    }`}
                    style={{ maxWidth: '44px', wordBreak: 'break-word' }}
                  >
                    {titleLabel}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-px flex-1 mx-0.5 mb-3.5 ${isDone ? 'bg-teal-300' : 'bg-slate-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProvincialGr912202202Sem = ({ reportId, backUrl, contextInfo, defaultsGroupId, onFormChange, onRequestSave } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const getInitialPage = (step) => step <= 7 ? 1 : step <= 11 ? 2 : 3;
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const saveIntervalRef = useRef(null);
  const isDirtyRef = useRef(false);
  const flushSaveRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Restore saved data and generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDFProvincialGr912(values).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc9to12 fill]', err));
          })
          .catch(() => fillPDFProvincialGr912(form.getValues()).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc9to12 fill]', err)))
      );
      return;
    }
    if (defaultsGroupId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-groups/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = { ...form.getValues(), ...(json.reportDefaults || {}) };
            form.reset(values);
            fillPDFProvincialGr912(values).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc9to12 fill]', err));
          })
          .catch(() => fillPDFProvincialGr912(form.getValues()).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc9to12 fill]', err)))
      );
      return;
    }
    const saved = localStorage.getItem('formData-provincial-gr9-12-2022-02-sem-v1');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDFProvincialGr912(initialValues).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc9to12 fill]', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets + per-field bounding boxes for highlight
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 7 ? 0 : step <= 11 ? 1 : 2;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error('no widgets');
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_INDEX_FOR_SCHEMA_FIELD[schemaField] ?? 0;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error('Failed to build PDF scroll targets:', e);
      }
    }
    buildTargets();
  }, []);

  // Auto-save (300ms) + live PDF regeneration with highlight (500ms)
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (name === '_completedFields') return;

      isDirtyRef.current = true;

      if (name) lastChangedFieldRef.current = name;

      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        try {
          const url = await fillPDFProvincialGr912(form.getValues(), highlightRect);
          setFilledPDF(url);
        } catch (err) {
          console.error('[pvrc9to12 fill]', err);
        }
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
     
  }, [form]);

  flushSaveRef.current = () => {
    if (!isDirtyRef.current) return;
    isDirtyRef.current = false;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ data: form.getValues() }),
        })
      );
    } else if (defaultsGroupId) {
      onFormChange?.(form.getValues());
    } else {
      localStorage.setItem('formData-provincial-gr9-12-2022-02-sem-v1', JSON.stringify(form.getValues()));
    }
  };

  useEffect(() => {
    saveIntervalRef.current = setInterval(() => flushSaveRef.current?.(), 30_000);
    return () => {
      clearInterval(saveIntervalRef.current);
      flushSaveRef.current?.();
    };
  }, []);  

  const handleDownload = async () => {
    flushSaveRef.current?.();
    if (!filledPDF) return;
    const cleanUrl = await fillPDFProvincialGr912(form.getValues(), null);
    const { studentName, oen } = form.getValues();
    const fileName = [studentName, oen].filter(Boolean).join('_').replace(/\s+/g, ' ').trim() || 'provincial-report-card-gr9-12';
    const a = document.createElement('a');
    a.href = cleanUrl;
    a.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNext = () => {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  };
  const handlePrev = () => {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  };

  const toggleManualComplete = (stepId) => {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues('_completedFields') || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue('_completedFields', [...current]);
  };


  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((s) => s.id)
  );

  // Dynamic labels for the course sub-row: use truncated course title when available
  const courseLabels = Object.fromEntries(
    [1,2,3,4,5,6,7,8].map((n) => [n, watchedValues[`course${n}Title`] || ''])
  );

  // ── Step content renderer ─────────────────────────────────────────────────
  const renderStep = () => {
    const { control } = form;

    // ── Step 1: Student Info ────────────────────────────────────────────────
    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" subtitle="Applies to all pages of the report" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Report Header</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { name: 'semester',        label: 'Semester',          colSpan: 1 },
                { name: 'reportingPeriod', label: 'Reporting Period',  colSpan: 1 },
                { name: 'date',            label: 'Date',              colSpan: 1 },
              ].map(({ name, label }) => (
                <FormField key={name} control={control} name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Student Details</p>
            <FormFieldGrid
              fields={[
                { name: 'studentName',       label: 'Student Name',       colSpan: 2 },
                { name: 'oen',               label: 'OEN'                             },
                { name: 'grade',             label: 'Grade'                           },
                { name: 'homeroom',          label: 'Homeroom'                        },
                { name: 'principal',         label: 'Principal'                       },
                { name: 'studentAddress',    label: 'Student Address',    colSpan: 2 },
                { name: 'schoolCouncilChair',label: 'School Council Chair',colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    // ── Step 2: School Info ─────────────────────────────────────────────────
    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School Information" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <FormFieldGrid
              fields={[
                { name: 'schoolName',      label: 'School Name',    colSpan: 2 },
                { name: 'schoolTelephone', label: 'Telephone'                   },
                { name: 'schoolFax',       label: 'Fax'                         },
                { name: 'schoolAddress',   label: 'School Address', colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    // ── Step 3: Board Info ──────────────────────────────────────────────────
    if (currentStep === 3) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Board Information" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <FormFieldGrid
              fields={[
                { name: 'boardName',         label: 'Board Name',      colSpan: 2 },
                { name: 'boardEmailWebsite', label: 'Email / Website', colSpan: 2 },
                { name: 'boardAddress',      label: 'Board Address',   colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    // ── Steps 4–11: Course steps ────────────────────────────────────────────
    if (currentStep >= 4 && currentStep <= 11) {
      const courseNum = currentStep - 3;
      const semLabel = courseNum <= 4 ? 'Semester 1' : 'Semester 2';
      const courseKey = `course${courseNum}`;
      return (
        <div className="space-y-4">
          <SectionHeading
            title={`Course ${courseNum}`}
            subtitle={semLabel}
          />
          <CourseCard
            courseNum={courseNum}
            semLabel={semLabel}
            form={form}
            reportId={reportId}
          />
        </div>
      );
    }

    // ── Step 12: Credit Summary ─────────────────────────────────────────────
    if (currentStep === 12) {
      return (
        <div className="space-y-5">
          <SectionHeading
            title="Credit Summary"
            subtitle="Total credits earned this report and to date"
          />

          {/* Credit table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Column headers */}
            <div className="grid bg-slate-50 border-b border-slate-200 px-5 py-2.5"
              style={{ gridTemplateColumns: '1fr 80px 80px' }}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subject / Category</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-teal-600 text-center">This Report</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">To Date</span>
            </div>

            {CREDIT_ROWS.map((row, i) => (
              <div
                key={row.thisReportField}
                className={`grid items-center px-5 py-2.5 ${i < CREDIT_ROWS.length - 1 ? 'border-b border-slate-100' : ''} ${row.isTotal ? 'bg-teal-50 font-semibold' : ''}`}
                style={{ gridTemplateColumns: '1fr 80px 80px' }}
              >
                <span className={`text-sm ${row.isTotal ? 'font-bold text-teal-700' : 'text-slate-700'}`}>
                  {row.label}
                </span>
                <FormField control={control} name={row.thisReportField}
                  render={({ field }) => (
                    <FormItem className="flex justify-center">
                      <FormControl>
                        <input {...field} maxLength={4} inputMode="numeric"
                          className="w-14 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField control={control} name={row.toDateField}
                  render={({ field }) => (
                    <FormItem className="flex justify-center">
                      <FormControl>
                        <input {...field} maxLength={4} inputMode="numeric"
                          className="w-14 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>

          {/* SHSM */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Specialist High Skills Major (SHSM)
            </p>
            <FormFieldGrid
              fields={[
                { name: 'specialistHighSkillsMajorName',             label: 'SHSM Name',       colSpan: 2 },
                { name: 'specialistHighSkillsMajorTotalRequired',    label: 'Total Required'               },
                { name: 'specialistHighSkillsMajorEarnedThisReport', label: 'Earned This Report'           },
                { name: 'specialistHighSkillsMajorEarnedToDate',     label: 'Earned To Date'               },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>

          {/* OSSL + School Use */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-4">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">OSSL Requirement</span>
              <FormField
                control={control}
                name="oSSLiteracyRequirementCompleted"
                render={({ field }) => (
                  <button
                    type="button"
                    onClick={() => field.onChange(field.value === 'yes' ? '' : 'yes')}
                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition-colors cursor-pointer ${
                      field.value === 'yes'
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    Completed
                  </button>
                )}
              />
            </div>
            <FormFieldGrid
              fields={[
                { name: 'schoolUse',              label: 'School Use',     colSpan: 2 },
                { name: 'principalSignatureDate', label: 'Signature Date', colSpan: 2 },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const isShared = backUrl?.startsWith('/shared-classes');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || '/'}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        <div className="text-center">
          <h1
            className="text-lg font-semibold text-slate-800"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Ontario Provincial Report Card - Gr. 9–12
          </h1>
          {contextInfo && (
            <p className="text-xs text-slate-500 leading-none mt-0.5">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear, contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {defaultsGroupId && (
            <Button size="sm" onClick={() => onRequestSave?.(form.getValues())} className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600">
              Save Defaults
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} className="gap-1.5">
            {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
            <span className="text-xs">{showPreview ? 'Hide PDF' : 'Show PDF'}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class - changes you save are visible to the owner
        </div>
      )}

      {/* Class Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 border-b-2 border-blue-200 shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-blue-500" />
          <span className="text-sm font-medium text-blue-800">
            Class Defaults — <span className="font-normal">You&apos;re setting default values for all reports in this class. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? 'w-[55%]' : 'w-full'}`}>
          {/* Step indicator */}
          <div className="px-5 pt-4 pb-2 shrink-0 border-b border-slate-100 bg-white">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
              courseLabels={courseLabels}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && !defaultsGroupId && (
            <div className="px-5 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className="flex-1 overflow-y-auto px-5 py-5 bg-slate-50">
            <Form {...form}>
              <form>{renderStep()}</form>
            </Form>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>
            <div className="flex items-center gap-2">
              {!isShared && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? 'default' : 'outline'}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? 'gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : 'gap-1.5'}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? 'Marked Complete' : 'Mark Complete'}
                </Button>
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                  <LucideDownload size={16} className="mr-1.5" />
                  {isPdfGenerating ? 'Updating…' : 'Download PDF'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${showPreview ? 'w-[45%]' : 'w-0'}`}>
          {showPreview && (
            <PDFPreview src={filledPDF} scrollTarget={scrollTargets[currentStep]} defaultPageCount={3} />
          )}
        </div>
      </div>
    </div>
  );
};
